/**
 * Telas de referência de alíquotas — a matriz origem × destino e as internas.
 *
 * Os números vêm do backend (`/referencia/matriz` e `/referencia/ufs`). A fonte
 * das internas são as fichas Econet por UF (`legislacao/aliquotas_icms`); a
 * matriz não tem base própria: a diagonal é a mesma interna e o resto é a regra
 * 7%/12% do motor (`aliquota`), então as duas abas sempre batem.
 */

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useMatriz, useUfs } from "@/hooks/queries";
import { pct, pctCurto } from "@/lib/format";
import { cn } from "@/lib/utils";

export function MatrizIcms() {
  const { data, isLoading } = useMatriz();
  const { data: refUfs } = useUfs();
  const navegar = useNavigate();

  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;

  /*
   * Como na aplicação original, a célula é um atalho: leva o par origem→destino
   * para a Venda à Ordem. O montador vive dentro da outra rota, então o par
   * viaja pela query string e é consumido lá (ver `MontadorOperacao`).
   */
  const levarAoFluxo = (origem: string, destino: string) => {
    navegar(`/vendas-ece?origem=${origem}&destino=${destino}`);
    toast.success(
      `Venda à ordem com produtor no ${origem} e cliente no ${destino}.`,
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Matriz origem × destino — EHC</CardTitle>
        <CardDescription>
          Linhas = <b>origem</b> · colunas = <b>destino</b>. São as alíquotas
          que os roteiros aplicam. Clique numa célula para levar o par ao fluxo
          de venda à ordem.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/*
          27x27: a grade inteira precisa caber na tela sem rolagem. `table-fixed`
          + `w-full` reparte a largura do card entre as 28 colunas em vez de
          deixar o conteúdo mandar, e a fonte miúda garante que o pior caso
          ("10,54") caiba na coluna. O `min-w` só entra em cena no celular, onde
          uma matriz desse tamanho não tem como caber — aí volta a rolar, e a
          coluna das origens fica grudada à esquerda para não se perder o rumo.
        */}
        <div className="overflow-x-auto rounded-lg border border-border/70 p-1">
          <table className="numerico w-full min-w-[58rem] table-fixed border-separate border-spacing-0.5 text-[10px] xl:text-[11px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-8 bg-card" />
                {data.ufs.map((destino) => (
                  <th
                    key={destino}
                    className="bg-card pb-1 font-extrabold text-fs-escuro"
                  >
                    {destino}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.ufs.map((origem) => (
                <tr key={origem}>
                  <th className="sticky left-0 z-10 bg-card pr-1 text-right font-extrabold text-fs-escuro">
                    {origem}
                  </th>
                  {data.ufs.map((destino) => {
                    const valor = data.matriz[origem]?.[destino] ?? 0;
                    const interna = origem === destino;
                    return (
                      <td
                        key={destino}
                        onClick={() => levarAoFluxo(origem, destino)}
                        title={`${refUfs?.nomes[origem] ?? origem} → ${
                          refUfs?.nomes[destino] ?? destino
                        }: ${pct(valor)} — clique para montar a venda à ordem`}
                        className={cn(
                          "cursor-pointer whitespace-nowrap rounded px-0.5 py-1 text-center leading-none",
                          "transition-transform duration-150 hover:scale-125 hover:ring-1 hover:ring-primary/50",
                          interna
                            ? "bg-fs-escuro font-extrabold text-fs-menta"
                            : valor === 0.07
                              ? "bg-emerald-100"
                              : "bg-secondary",
                        )}
                      >
                        {pctCurto(valor)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded bg-emerald-100" /> 7% interestadual
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded border border-border bg-secondary" />{" "}
            12% interestadual
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded bg-fs-escuro" /> operação interna
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Casos em que a alíquota da tabela não conta a história inteira: carga, diferimento
 * ou ST que mudam o ICMS destacado. Resumo dos textos das regras de cada UF
 * (`backend/app/regras/dados/uf/*.toml`), em ordem alfabética.
 */
const OBSERVACOES_INTERNAS = [
  {
    uf: "AL",
    texto:
      "Os 21,5% somam a alíquota de 20,5% e o adicional de 1% do FECOEP.",
    fundamento: "Lei 5.900/1996, art. 17, I, b; Lei 6.558/2004, art. 2º-A",
  },
  {
    uf: "BA",
    texto:
      "12,86% até 31/10/2026. A partir de 01/11/2026, com a revogação do Decreto 21.521/2022, vale a regra geral de 20,5%.",
    fundamento: "Dec. 21.521/2022, art. 1º; Dec. 24.236/2025, art. 2º, II; Lei 7.014/1996, art. 15, I, a",
  },
  {
    uf: "ES",
    texto:
      "A ficha Econet dá 27% desde 23/03/2025, mas o app aplica 17%: o § 7º do art. 20 da Lei 7.000/2001 suspende as alíquotas específicas dos combustíveis enquanto vigorar a LC 194/2022.",
    fundamento: "Lei 7.000/2001, art. 20, § 7º (red. Lei 12.320/2024)",
  },
  {
    uf: "GO",
    texto:
      "Na venda da usina ao distribuidor de GO, o distribuidor é substituto pelo ICMS sobre 80% do valor da operação; a usina recolhe sobre os 20% restantes.",
    fundamento: "RCTE/GO, Anexo VIII, art. 12, §§ 1º e 3º",
  },
  {
    uf: "MG",
    texto:
      "Na venda interna da usina a distribuidor ou ECE, o ICMS é diferido (CST 051) até a retenção da ST ou a saída do Estado.",
    fundamento: "RICMS/MG 2023, Anexo VII, art. 125",
  },
  {
    uf: "MS",
    texto:
      "Os 11,33% são a carga sobre a alíquota nominal de 17%. Nas saídas da destilaria para ECE, cooperativa ou distribuidora, e da ECE ou cooperativa para distribuidora, o ICMS é diferido (CST 051).",
    fundamento:
      "RICMS/MS, art. 41, III, g, e § 1º-A; Dec. 15.998/2022; Dec. 13.275/2011, art. 4º, II, c e d",
  },
  {
    uf: "MT",
    texto:
      "A usina credenciada aplica os 17% sobre a base reduzida a 62% do PMPF, o que dá carga de 10,54% do PMPF.",
    fundamento: "RICMS/MT, art. 486, e Anexo V, art. 35",
  },
  {
    uf: "PR",
    texto:
      "Diferimento parcial de 33,33% nas saídas da usina para ECE, cooperativa ou distribuidora, e da ECE ou cooperativa para distribuidora (ICMS devido de 8%); da distribuidora para o varejo, 38,889% (ICMS devido de 7,333%).",
    fundamento: "RICMS/PR, Anexo VIII, art. 31, itens 4.1 e 4.2",
  },
  {
    uf: "SP",
    texto:
      "Nas saídas internas entre distribuidores, o ICMS próprio é diferido até a saída a posto revendedor, consumidor final ou TRR.",
    fundamento: "RICMS/SP, art. 418-F",
  },
];

export function AliquotasInternas() {
  const { data, isLoading } = useUfs();
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;

  const ordenadas = [...data.ufs].sort(
    (a, b) => data.internas[a] - data.internas[b],
  );

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Alíquotas internas do EHC</CardTitle>
          <CardDescription>
            Alíquota interna do etanol hidratado por UF, em ordem crescente,
            conforme a legislação de cada estado. É esta que os roteiros aplicam
            nas operações internas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/*
          Grade elástica como no original: as fichas têm 150px de piso e o número
          de colunas acompanha a largura disponível, em vez de saltar em degraus
          fixos por breakpoint.
        */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2">
            {ordenadas.map((uf) => (
              <div
                key={uf}
                className="flex items-center justify-between rounded-lg border border-border/70 bg-secondary/50 px-3 py-2 transition-all duration-200 ease-suave hover:-translate-y-px hover:border-primary/35 hover:bg-card hover:shadow-baixa"
                title={data.nomes[uf]}
              >
                <span className="font-bold">{uf}</span>
                <span className="numerico font-extrabold text-fs-escuro">
                  {pct(data.internas[uf])}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Observações</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            {OBSERVACOES_INTERNAS.map(({ uf, texto, fundamento }) => (
              <li key={uf} className="flex gap-3">
                <span className="w-8 shrink-0 font-bold" title={data.nomes[uf]}>
                  {uf}
                </span>
                <span>
                  {texto}{" "}
                  <span className="text-muted-foreground">({fundamento})</span>
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
